// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
// helper for acs lists
import { DefaultApi, JsGetActiveContractsResponse } from '@digitalasset/json-api-ts-client';
import { WSSupport } from '../WSSupport';
import { ACSCache } from './ACSCache';
import { ACSCacheUtils } from './ACSCacheUtils';
import { ACSContainer } from './ACSContainer';
import { ACSHelperLoggerUtils } from './ACSHelperLoggerUtils';
import { ACSKey, SerializedACSContainer } from './ACSTypes';

export class ACSHelper {
  public static cacheLifespanMs: number = 6 * 60 * 60 * 1000; // 6 hour

  constructor(
    private readonly apiInstance: DefaultApi,
    private readonly wsSupport: WSSupport,
    private readonly cache: ACSCache | null = null,
  ) {
    /* eslint-disable */
    if (typeof window !== 'undefined') {
      (window as any)['acs'] = {
        printAllKnownKeys: () => {
          ACSHelperLoggerUtils.printAllKnownKeys(this.cache);
        },
        downloadContainerLogsByKey: (key: ACSKey) => {
          ACSHelperLoggerUtils.downloadLogByKey(key, this.cache);
        },
      };
    }
    /* eslint-enable */
  }

  public async clearAllCache(): Promise<void> {
    await this.cache?.clear();
  }

  async activeContractsForTemplate(
    offset: number,
    partyFilter: string,
    templateId: string,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    const keyData = ACSCacheUtils.createKeyData(partyFilter, templateId, undefined);
    const key = ACSCacheUtils.keyDataToString(keyData);

    const serializedContainer = await this.getCached(key);

    const container = new ACSContainer(keyData, serializedContainer);
    const result = await container.update(offset, this.apiInstance, this.wsSupport);

    await this.cache?.set(container.serialize());

    return result;
  }

  async activeContractsForInterface(
    offset: number,
    partyFilter: string,
    interfaceId: string,
  ): Promise<Array<JsGetActiveContractsResponse>> {
    const keyData = ACSCacheUtils.createKeyData(partyFilter, undefined, interfaceId);
    const key = ACSCacheUtils.keyDataToString(keyData);

    const serializedContainer = await this.getCached(key);

    const container = new ACSContainer(keyData, serializedContainer);
    const result = await container.update(offset, this.apiInstance, this.wsSupport);

    await this.cache?.set(container.serialize());

    return result;
  }

  private async getCached(key: ACSKey): Promise<SerializedACSContainer | null> {
    const serializedContainer = (await this.cache?.get(key)) ?? null;

    if (!serializedContainer || !serializedContainer.initialized) {
      return null;
    }

    const isCacheRecentEnough =
      serializedContainer.creationTimestampMs + ACSHelper.cacheLifespanMs > Date.now();

    if (!isCacheRecentEnough) {
      await this.cache?.remove(key);
      return null;
    }

    return serializedContainer;
  }
}
